import { 
  UIImage, 
  SupabaseUIImage, 
  CreateUIImageParams, 
  UpdateUIImageParams, 
  UIImageQueryParams,
  UIImageResult,
  UIImageListResult,
  StorageProvider,
  validateImageAspectRatio,
  UIImageCategory
} from './types';
import { Uid } from '@/utils/utils';
import { uploadAsset, convertAsset } from '@/modules/uploadWizard/services/UploadWizardService';
import { captureError } from '@/utils/sentry';
import { v1, toResult } from '@/infrastructure/api/v1/client';
import { listAll } from '@/infrastructure/api/v1/lists';
import { imageCreateBody, imagePatchBody, imageToLegacy, type ImageDto, type LegacyImage } from '@/infrastructure/api/v1/mappers/images';

function convertToUIImage(supabaseImage: SupabaseUIImage): UIImage {
  return {
    id: supabaseImage.id,
    createdAt: supabaseImage.created_at,
    imageId: supabaseImage.image_id,
    projectInstanceId: supabaseImage.project_instance_id,
    sceneOriginId: supabaseImage.scene_origin_id,
    sceneInstanceId: supabaseImage.scene_instance_id,
    imageCategory: supabaseImage.image_category as UIImageCategory,
    imageMetadata: supabaseImage.image_metadata,
    imageOriginalUrl: supabaseImage.image_original_url,
    imageCroppedUrl: supabaseImage.image_cropped_url,
    userId: supabaseImage.user_id,
    imageOrderNumber: supabaseImage.image_order_number,
    assetTypeId: supabaseImage.asset_type_id
  };
}

/** An image as the app holds it, from its v1 answer. v1 has no numeric row id, so UIImage.id is left unset. */
export function uiImageFromDto(dto: ImageDto): UIImage {
  return convertToUIImage(imageToLegacy(dto) as unknown as SupabaseUIImage);
}

export class UIImageService {
  constructor(
    private readonly storage: StorageProvider
  ) {}

  private fromDto(dto: ImageDto): UIImage {
    return uiImageFromDto(dto);
  }

  private convertToSupabaseUIImage(image: CreateUIImageParams): Partial<LegacyImage> {
    return {
      image_id: Uid(),
      project_instance_id: image.projectInstanceId,
      scene_origin_id: image.sceneOriginId,
      scene_instance_id: image.sceneInstanceId,
      image_category: image.imageCategory,
      image_metadata: image.imageMetadata === undefined ? undefined : JSON.stringify(image.imageMetadata),
      image_original_url: image.imageOriginalUrl,
      image_cropped_url: image.imageCroppedUrl,
      user_id: image.userId,
      image_order_number: image.imageOrderNumber,
      asset_type_id: image.assetTypeId
    };
  }

  async createUIImage(params: CreateUIImageParams): Promise<UIImageResult> {
    try {
      const supabaseData = this.convertToSupabaseUIImage(params);

      const { data, error } = await toResult(v1.POST('/api/v1/images', { body: imageCreateBody(supabaseData) }));

      if (error) {
        return {
          success: false,
          error: error.message
        };
      }

      return {
        success: true,
        data: this.fromDto(data)
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      };
    }
  }

  async getUIImage(imageId: string): Promise<UIImageResult> {
    try {
      const { data, error } = await toResult(v1.GET('/api/v1/images/{uniqueId}', { params: { path: { uniqueId: imageId } } }));

      if (error) {
        return {
          success: false,
          error: error.message
        };
      }

      return {
        success: true,
        data: this.fromDto(data)
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      };
    }
  }

  async updateUIImage(imageId: string, params: UpdateUIImageParams): Promise<UIImageResult> {
    try {
      const updateData: Partial<LegacyImage> = {};
      
      if (params.imageCategory !== undefined) {
        updateData.image_category = params.imageCategory;
      }
      if (params.imageMetadata !== undefined) {
        updateData.image_metadata = JSON.stringify(params.imageMetadata);
      }
      if (params.imageOriginalUrl !== undefined) {
        updateData.image_original_url = params.imageOriginalUrl;
      }
      if (params.imageCroppedUrl !== undefined) {
        updateData.image_cropped_url = params.imageCroppedUrl;
      }
      if (params.imageOrderNumber !== undefined) {
        updateData.image_order_number = params.imageOrderNumber;
      }
      if (params.assetTypeId !== undefined) {
        updateData.asset_type_id = params.assetTypeId;
      }

      const { data, error } = await toResult(v1.PATCH('/api/v1/images/{uniqueId}', {
        params: { path: { uniqueId: imageId } },
        body: imagePatchBody(updateData),
      }));

      if (error) {
        return {
          success: false,
          error: error.message
        };
      }

      return {
        success: true,
        data: this.fromDto(data)
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      };
    }
  }

  async deleteUIImage(imageId: string): Promise<UIImageResult> {
    try {
      const { error } = await toResult(v1.DELETE('/api/v1/images/{uniqueId}', { params: { path: { uniqueId: imageId } } }));

      if (error) {
        return {
          success: false,
          error: error.message
        };
      }

      return {
        success: true
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      };
    }
  }

  async getUIImages(params: UIImageQueryParams): Promise<UIImageListResult> {
    try {
      const { data, error } = await listAll(page => v1.GET('/api/v1/images', {
        params: {
          query: {
            projectUniqueId: params.projectInstanceId ? [params.projectInstanceId] : undefined,
            sceneOriginUniqueId: params.sceneOriginId ? [params.sceneOriginId] : undefined,
            sceneUniqueId: params.sceneInstanceId ? [params.sceneInstanceId] : undefined,
            category: params.imageCategory || params.imageCategories?.length
              ? [...(params.imageCategory ? [params.imageCategory] : []), ...(params.imageCategories ?? [])]
              : undefined,
            userUniqueId: params.userId || undefined,
            sort: '-createdAt',
            ...page,
          },
        },
      }));

      if (error) {
        return {
          success: false,
          error: error.message
        };
      }

      return {
        success: true,
        data: data.map(dto => this.fromDto(dto))
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      };
    }
  }

  /**
   * Fetches all UI images for a scene origin in a single query.
   * Use this instead of multiple getUIImages calls to avoid N+1 patterns.
   * @param sceneOriginId - The scene origin ID
   * @param includeTemplateCategories - If true, includes TemplateLandscape and SceneVideo (for template scenes)
   */
  async getUIImagesForSceneOrigin(
    sceneOriginId: string,
    includeTemplateCategories: boolean = false
  ): Promise<UIImageListResult> {
    const categories: UIImageCategory[] = [
      'SceneLandscape',
      'ScanImage',
      'ImageAnchor',
      ...(includeTemplateCategories ? (['TemplateLandscape', 'SceneVideo'] as UIImageCategory[]) : [])
    ];

    try {
      const { data, error } = await listAll(page => v1.GET('/api/v1/images', {
        params: { query: { sceneOriginUniqueId: [sceneOriginId], category: categories, sort: '-createdAt', ...page } },
      }));

      if (error) {
        return {
          success: false,
          error: error.message
        };
      }

      return {
        success: true,
        data: data.map(dto => this.fromDto(dto))
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      };
    }
  }

  async uploadUIImage(
    file: File,
    params: Omit<CreateUIImageParams, 'imageUrl'>,
    validateAspectRatio: boolean = true
  ): Promise<UIImageResult> {
    try {
      // Validate aspect ratio if category is specified and validation is enabled
      if (validateAspectRatio && params.imageCategory) {
        const validation = await validateImageAspectRatio(file, params.imageCategory);
        if (!validation.isValid) {
          return {
            success: false,
            error: validation.error || 'Aspect ratio validation failed'
          };
        }
      }

      // Generate unique filename
      const fileExtension = file.name.split('.').pop();
      const fileName = `${params.imageCategory || 'ui'}-${Date.now()}.${fileExtension}`;
      const filePath = `ui-images/${params.projectInstanceId || 'general'}/${fileName}`;

      // Upload file to storage
      const { data: stored, error: uploadError } = await this.storage.uploadFile(filePath, file, {
        contentType: file.type,
        cacheControl: '3600',
        upsert: false
      });

      if (uploadError || !stored) {
        return {
          success: false,
          error: `Upload failed: ${uploadError?.message ?? 'no address answered'}`
        };
      }

      // Create database record, at the address the upload answered
      const createParams: CreateUIImageParams = {
        ...params,
        imageOriginalUrl: stored.url
      };

      return await this.createUIImage(createParams);
    } catch (error) {
      captureError(error, { domain: 'uiImages', operation: 'uploadUIImage', category: params.imageCategory });
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      };
    }
  }

  // Download an externally-hosted image (e.g. a Google Street View API URL that is
  // generated on the fly and won't resolve on device) and re-host it in storage.
  // Returns the stable public URL, or null on failure so callers can fall back.
  async persistExternalImage(
    sourceUrl: string,
    params: { projectInstanceId?: string; sceneInstanceId?: string; userId?: string; category?: string }
  ): Promise<string | null> {
    try {
      const response = await fetch(sourceUrl);
      if (!response.ok) return null;
      const blob = await response.blob();
      if (!blob.size) return null;

      const fileExtension = (blob.type.split('/')[1] || 'png').split('+')[0];
      const fileName = `${params.category || 'image'}-${Date.now()}.${fileExtension}`;
      // With no project or scene in hand the image goes in the user's own folder
      const scope = params.projectInstanceId || params.sceneInstanceId || params.userId;
      if (!scope) return null;
      const filePath = `ui-images/${scope}/${fileName}`;

      const { data: stored, error: uploadError } = await this.storage.uploadFile(filePath, blob, {
        contentType: blob.type,
        cacheControl: '3600',
        upsert: false
      });
      if (uploadError || !stored) return null;

      return stored.url;
    } catch (error) {
      captureError(error, { domain: 'uiImages', operation: 'persistExternalImage', category: params.category });
      return null;
    }
  }

  async uploadUIVideo(
    file: File,
    params: Omit<CreateUIImageParams, 'imageUrl'>,
    maxSizeMB: number = 500
  ): Promise<UIImageResult> {
    try {
      // Validate file size
      const maxSizeBytes = maxSizeMB * 1024 * 1024;
      if (file.size > maxSizeBytes) {
        return {
          success: false,
          error: `File size exceeds maximum of ${maxSizeMB}MB`
        };
      }

      // Validate file type
      const allowedTypes = ['video/mp4', 'video/webm', 'video/quicktime', 'video/x-msvideo'];
      const allowedExtensions = ['.mp4', '.webm', '.mov', '.avi'];
      const hasValidType = allowedTypes.includes(file.type) || 
                          allowedExtensions.some(ext => file.name.toLowerCase().endsWith(ext));
      
      if (!hasValidType) {
        return {
          success: false,
          error: 'File must be a video (MP4, WebM, MOV, or AVI)'
        };
      }

      // Generate unique filename
      const fileExtension = file.name.split('.').pop();
      const fileName = `${params.imageCategory || 'ui-video'}-${Date.now()}.${fileExtension}`;
      const filePath = `ui-videos/${params.projectInstanceId || params.sceneOriginId || 'general'}/${fileName}`;

      // Upload file to storage
      const { data: stored, error: uploadError } = await this.storage.uploadFile(filePath, file, {
        contentType: file.type,
        cacheControl: '3600',
        upsert: false
      });

      if (uploadError || !stored) {
        return {
          success: false,
          error: `Upload failed: ${uploadError?.message ?? 'no address answered'}`
        };
      }

      // Create database record (using imageOriginalUrl for video URL), at the address the upload answered
      const createParams: CreateUIImageParams = {
        ...params,
        imageOriginalUrl: stored.url
      };

      return await this.createUIImage(createParams);
    } catch (error) {
      captureError(error, { domain: 'uiImages', operation: 'uploadUIVideo', category: params.imageCategory });
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      };
    }
  }

  async deleteUIImageWithFile(imageId: string): Promise<UIImageResult> {
    try {
      // First get the image to get the file path
      const getResult = await this.getUIImage(imageId);
      if (!getResult.success || !getResult.data) {
        return getResult;
      }

      // Delete from database first
      const deleteResult = await this.deleteUIImage(imageId);
      if (!deleteResult.success) {
        return deleteResult;
      }

      // TODO: Delete file from storage if needed
      // This would require implementing file deletion in the storage provider

      return {
        success: true
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      };
    }
  }

  // Helper function to extract filename from URL
  private getFilenameFromUrl(url: string): string {
    const urlParts = url.split('/');
    const filenameWithParams = urlParts[urlParts.length - 1] || 'image-anchor';
    
    // Remove query parameters (everything after ?)
    const filename = filenameWithParams.split('?')[0];
    
    // Ensure it ends with .png
    if (!filename.toLowerCase().endsWith('.png')) {
      return filename.replace(/\.[^/.]+$/, '') + '.png';
    }
    
    return filename;
  }

  // Helper function to get display name (filename with extension)
  private getDisplayName(filename: string): string {
    return filename; // Keep the full filename including extension
  }

  // Helper function to convert URL to File object
  private async urlToFile(url: string, filename: string): Promise<File> {
    const response = await fetch(url);
    const blob = await response.blob();
    return new File([blob], filename, { type: blob.type });
  }

  // Create ImageAnchor asset with both asset_type and ui_images records
  async createImageAnchorAsset(params: {
    sceneOriginId: string;
    projectInstanceId?: string;
    orgInstanceId?: string;
    imageOriginalUrl: string;
    imageCroppedUrl: string;
    crop: { x: number; y: number; width: number; height: number };
    userId: string;
  }): Promise<{
    success: boolean;
    data?: {
      uiImageId: string;
      assetTypeId: string;
    };
    error?: string;
  }> {
    try {
      // Step 1: Convert cropped image URL to File object for upload
      const filename = this.getFilenameFromUrl(params.imageCroppedUrl);
      const file = await this.urlToFile(params.imageCroppedUrl, filename);

      // Step 2: Upload using the backend API
      const uploadResult = await uploadAsset({
        file: file,
        assetTypeKey: 'imageAnchor',
        assetName: this.getDisplayName(filename),
        userId: params.userId,
        orgInstanceId: params.orgInstanceId,
        availability: 'private' // Image anchors are typically private
      });

      if (!uploadResult.success) {
        return {
          success: false,
          error: uploadResult.error || 'Upload failed'
        };
      }

      const assetTypeId = uploadResult.generatedAssetTypeId;

      // Step 3: Start conversion in background (don't wait for completion)
      convertAsset(assetTypeId).catch(error => {
        console.error('Background conversion failed for ImageAnchor:', error);
      });

      // Step 4: Create UI Image Record with Asset Reference
      const uiImageParams = {
        sceneOriginId: params.sceneOriginId,
        projectInstanceId: params.projectInstanceId,
        imageCategory: 'ImageAnchor' as UIImageCategory,
        imageOriginalUrl: params.imageOriginalUrl,
        imageCroppedUrl: params.imageCroppedUrl,
        assetTypeId: assetTypeId,
        userId: params.userId,
        imageMetadata: { crop: params.crop }
      };

      const uiImageResult = await this.createUIImage(uiImageParams);
      if (!uiImageResult.success) {
        // Note: We can't easily delete the asset from the backend API here
        // The asset will remain in the system but won't be linked to a UI image
        return {
          success: false,
          error: uiImageResult.error
        };
      }

      return {
        success: true,
        data: {
          uiImageId: uiImageResult.data!.imageId,
          assetTypeId: assetTypeId
        }
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      };
    }
  }

  // Update existing ImageAnchor asset (creates new asset and updates UI image reference)
  async updateImageAnchorAsset(params: {
    uiImageId: string;
    imageOriginalUrl: string;
    imageCroppedUrl: string;
    crop: { x: number; y: number; width: number; height: number };
    userId: string;
    orgInstanceId?: string;
  }): Promise<{ success: boolean; error?: string }> {
    try {
      // Step 1: Get Current UI Image
      const currentUiImage = await this.getUIImage(params.uiImageId);
      if (!currentUiImage.success || !currentUiImage.data?.assetTypeId) {
        return {
          success: false,
          error: 'UI image or asset reference not found'
        };
      }

      // Step 2: Create new asset with updated image using backend API
      const filename = this.getFilenameFromUrl(params.imageCroppedUrl);
      const file = await this.urlToFile(params.imageCroppedUrl, filename);

      const uploadResult = await uploadAsset({
        file: file,
        assetTypeKey: 'imageAnchor',
        assetName: this.getDisplayName(filename),
        userId: params.userId,
        orgInstanceId: params.orgInstanceId,
        availability: 'private'
      });

      if (!uploadResult.success) {
        return {
          success: false,
          error: uploadResult.error || 'Upload failed'
        };
      }

      const newAssetTypeId = uploadResult.generatedAssetTypeId;

      // Start conversion in background (don't wait for completion)
      convertAsset(newAssetTypeId).catch(error => {
        console.error('Background conversion failed for updated ImageAnchor:', error);
      });

      // Step 3: Update UI Image with new asset reference
      const uiImageUpdateResult = await this.updateUIImage(params.uiImageId, {
        imageOriginalUrl: params.imageOriginalUrl,
        imageCroppedUrl: params.imageCroppedUrl,
        assetTypeId: newAssetTypeId,
        imageMetadata: { crop: params.crop }
      });
      if (!uiImageUpdateResult.success) {
        return {
          success: false,
          error: uiImageUpdateResult.error
        };
      }

      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      };
    }
  }

  // Delete ImageAnchor asset (UI image deletion will cascade to asset)
  async deleteImageAnchorAsset(uiImageId: string): Promise<{ success: boolean; error?: string }> {
    try {
      // Get UI image to verify it exists and has asset reference
      const uiImage = await this.getUIImage(uiImageId);
      if (!uiImage.success || !uiImage.data?.assetTypeId) {
        return {
          success: false,
          error: 'UI image or asset reference not found'
        };
      }

      // Delete UI image - cascade will automatically delete the asset
      const deleteResult = await this.deleteUIImage(uiImageId);
      if (!deleteResult.success) {
        return {
          success: false,
          error: deleteResult.error
        };
      }

      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      };
    }
  }
}
